/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import translate from 'services/i18n/translate';
import { SimpleHeader, Spinner, Button, Modal } from 'ui/components';
import { StartUpRoutePath } from 'routes/paths';
import { Ducks, XError } from '@wit/vtv-x-foundation-lib';
import { addInitializationErrorEvent } from 'store/ducks/analytics/systemEvent';
import { ButtonType } from 'ui/components/Button';
import AppConfig from 'services/AppConfig';

const usernameMaxLength = 40;
const passwordMaxLength = 20;

let loginRequestSent = false; // flag used to ignore previous state errors in the login duck

function renderErrorMessage(error?: XError, isLoggingIn?: boolean): JSX.Element | null {
  if (!error || isLoggingIn || !loginRequestSent) {
    return null;
  }

  return (
    <span className="generic-message">
      <span id="Login_errorIcon" className="icon icon-error" />
      <span id="Login_errorMessage" className="message">
        {error.translation}
      </span>
    </span>
  );
}

function Login(): JSX.Element {
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const routerHistory = useHistory();

  const isAuthenticated = useSelector(Ducks.Credentials.Selectors.isAuthenticated);
  const isLoggingIn = useSelector(Ducks.Credentials.Selectors.isCredentialsProcessing);
  const loginError = useSelector(Ducks.Credentials.Selectors.getError);
  const [recoverLinkObject, setRecoverLinkObject] = useState<string>();
  const [openRecoverModal, setOpenRecoverModal] = useState<boolean>(false);

  // Retrieve data from env file
  const forgotEmailObj = AppConfig.env?.SIGNIN_LINKS.FORGOT_EMAIL;
  const forgotPasswdObj = AppConfig.env?.SIGNIN_LINKS.FORGOT_PASSWORD;
  const manageAccountObj = AppConfig.env?.SIGNIN_LINKS.MANAGE_ACCOUNT;

  // Clean Modal Object on each close
  useEffect(() => {
    if (!openRecoverModal) {
      setRecoverLinkObject(undefined);
    }
  }, [openRecoverModal]);

  useEffect(() => {
    if (loginError && loginRequestSent) {
      addInitializationErrorEvent(loginError);
    }
  }, [loginError]);

  const handleLogin = () => {
    if (!isAuthenticated && usernameRef.current?.value && passwordRef.current?.value) {
      dispatch(Ducks.Credentials.Creators.login(usernameRef.current.value, passwordRef.current.value));
      loginRequestSent = true;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggingIn) {
      handleLogin();
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      routerHistory.replace(StartUpRoutePath.ROOT);
    }
  }, [routerHistory, isAuthenticated]);

  const openForgotHandler = (obj: string) => {
    if (obj) {
      setRecoverLinkObject(obj);
      setOpenRecoverModal(true);
    }
  };

  return (
    <div className="authentication sign-in overlay" id="Login">
      <Modal isShowing={openRecoverModal} setIsShowing={setOpenRecoverModal} iframeUrl={recoverLinkObject} />
      <SimpleHeader subtitle={translate.branding} showBrandLogo />
      <div className="component no-background">
        <Spinner isActive={isLoggingIn} isEmbedded />

        {renderErrorMessage(loginError, isLoggingIn)}

        <form className="group" onSubmit={handleSubmit}>
          <input
            id="Login_input___username"
            ref={usernameRef}
            type="text"
            name="username"
            disabled={isLoggingIn}
            maxLength={usernameMaxLength}
            placeholder={translate['signin.username']}
          />

          <input
            id="Login_input___password"
            ref={passwordRef}
            type="password"
            name="password"
            disabled={isLoggingIn}
            autoComplete="off"
            maxLength={passwordMaxLength}
            placeholder={translate['signin.password']}
          />

          <Button
            bemBlock="Login"
            bemModifier="signIn"
            buttonType={ButtonType.SUBMIT}
            disabled={isLoggingIn}
            className="margin-top-16"
            onClick={handleLogin}
            text={translate['signin.buttonLabel']}
          />
        </form>

        {AppConfig.env.LOGIN_WITH_CODE ? (
          <div className="btn-group actions">
            <span className="vtw-text-separator margin-bottom-20">{translate.textSeparator}</span>
            <Button
              bemBlock="Login"
              bemModifier="loginWithCode"
              buttonType={ButtonType.LINK}
              text={translate.logInWithCode}
              className="margin-right-0 margin-bottom-30"
              linkPath={StartUpRoutePath.CODE}
            />
          </div>
        ) : null}

        <div className="links-container">
          <ul id="Login_linksContainer">
            {forgotPasswdObj ? (
              <li>
                <a
                  id="Login_link___forgotPassword"
                  className="link-modal forgot-password"
                  onClick={() => {
                    openForgotHandler(forgotPasswdObj);
                  }}
                >
                  <span>{translate['signin.forgotPassword']}</span>
                </a>
              </li>
            ) : null}
            {forgotEmailObj ? (
              <li>
                <a
                  id="Login_link___forgotEmail"
                  className="link-modal forgot-email"
                  onClick={() => {
                    openForgotHandler(forgotEmailObj);
                  }}
                >
                  <span>{translate['signin.forgotEmail']}</span>
                </a>
              </li>
            ) : null}

            {manageAccountObj ? (
              <li>
                <a
                  id="Login_link___selfcareUrl"
                  href={manageAccountObj}
                  target="_blank"
                  className="link-modal"
                  rel="noreferrer"
                >
                  <span>{translate.signInSelfCareUrl}</span>
                </a>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Login);
